/** @jsxImportSource @emotion/react */
import { useScanEditingContextSlice, useUsers } from '@kaiizen/kai-pro-api-react-state';
import type { ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { PhotoDateEditor, PhotoDetailModal } from '@/Client/Partials/PhotoDetail';
import { getPhotoDate } from '@/Client/Partials/PhotoDetail/photoDetailLib';

import type { GalleryPhotoOccurrence } from '../galleryPhotoOperations';
import { buildPhotoManagerDetailModel } from './photoManagerDetailModel';

/**
 * The photo detail overlay, opened by clicking a photo in the takeover.
 *
 * The mockup's `_pmPhotoModeHtml` reuses its `po-*` lightbox inside the takeover rather than
 * inventing a second one, so this renders the shared PhotoDetailModal. The takeover keeps its own
 * model builder; only the presentation is shared.
 *
 * No task-review actions are passed: the manager acts on photos, not on the task the photo happens
 * to hang off, so the overlay renders without its approve/request-edit/delete row.
 */
export const PhotoManagerDetailOverlay = ({
  occurrence,
  occurrences,
  sectionPhotos,
  task,
  groupPhotos,
  groupTitle,
  notes,
  footer,
  onSetPhotoDate,
  onClose,
}: {
  occurrence: GalleryPhotoOccurrence;
  occurrences: readonly GalleryPhotoOccurrence[];
  /** The photo's own section, shown as the overlay's first rail. */
  sectionPhotos?: readonly GalleryPhotoOccurrence[];
  /** The task the photo hangs off, when it hangs off one. */
  task?: ScanTaskOptimized;
  /** The group's own photos, shown as the overlay's group rail. */
  groupPhotos?: readonly GalleryPhotoOccurrence[];
  groupTitle?: string;
  notes?: string;
  /** The takeover's own actions on this photo, for the pane's footer. */
  footer?: ReactNode;
  /** When set, the walk date is editable: the testing-tools way to place a photo on any day. */
  onSetPhotoDate?: (addedAt: number) => void | Promise<void>;
  onClose: () => void;
}) => {
  /* The modal owns which photo is on the stage; `onActiveThumbnailChange` reports it here, on open
     and on every step through the strip, so the rails' `selected` flags follow the reader. */
  const [activeKey, setActiveKey] = useState<string | undefined>(occurrence.key);
  useEffect(() => setActiveKey(occurrence.key), [occurrence.key]);
  /* Who took the photo and who is doing the work: names, not ids. Both were unresolved here — the
     user map was a prop nothing supplied, and the contractor needs the scan's defaults-by-trade,
     so without this context every task read as unassigned. */
  const { usersById } = useUsers();
  const { getContractorForTask } = useScanEditingContextSlice(['getContractorForTask']);
  const userNameById = useMemo(
    () => Object.fromEntries(Object.entries(usersById ?? {}).map(([userId, user]) => [userId, user.name])),
    [usersById],
  );
  const contractorContext = useMemo(() => ({ getContractorForTask }), [getContractorForTask]);

  return (
    <PhotoDetailModal
      model={buildPhotoManagerDetailModel({
        occurrence,
        occurrences,
        sectionPhotos,
        task,
        groupPhotos,
        groupTitle,
        notes,
        selectedKey: activeKey,
        userNameById,
        contractorContext,
        walkDateControl: onSetPhotoDate
          ? <PhotoDateEditor addedAt={getPhotoDate(occurrence.photo)} onCommit={onSetPhotoDate} />
          : undefined,
      })}
      footer={footer}
      onActiveThumbnailChange={setActiveKey}
      onClose={onClose}
      /* The takeover is already the overlay; the detail fills it rather than stacking a second
         centred card on top of it. */
      presentation={'fill'}
    />
  );
};
